import { lazy } from 'react'

/**
 * Módulo: Eventos (Club Milbrands — ola 3, adelantada el 2-sep-2026)
 *
 * Eventos presenciales y virtuales del Club: tipos de entrada por evento,
 * venta con Mercado Pago (tajadas T7+), landing pública, check-in por QR y
 * trazabilidad inscripto → pagó → asistió. Plan rector completo en
 * `docs/PLAN_eventos-club.md`; decisiones durables en el ADR
 * `2026-09-02-eventos-del-club-adr-rector-modelo-pared-horaria-y-candados`.
 *
 * Vendible por separado: un tenant puede contratar Eventos sin Formación,
 * Comunidad ni Empleo. El ítem del sidebar lo aporta el grupo `club`
 * (src/modules/club/config.js) — por eso acá NO se declaran menuItems: si los
 * declarara, el ítem aparecería dos veces (precedente exacto de `formacion`).
 *
 * Vista admin y vista miembro conviven en el mismo módulo, gateadas por
 * función del catálogo de permisos — mismo patrón que `formacion` y `casos`.
 * Desde el 6-sep-2026 el catálogo tiene DOS módulos: `eventos` (Asistir — este,
 * el de la ruta y el sidebar) y `eventos-organizar` (Organizar — ítem
 * contratable sin UI, `src/modules/eventos-organizar/config.js`). La vista
 * admin se gatea por acciones de ORGANIZAR (`puede('eventos-organizar', …)`);
 * el plan que la habilita lo exige el server.
 *
 * Acceso: NUNCA se lee `empresa.plan` ni `empresaIds` desde este módulo. Todo
 * pasa por el resolver horizontal de entitlements; la audiencia por PLAN se
 * declara en el evento y en cada tipo de entrada, y la valida el server.
 */
export default {
  id: 'eventos',
  nombre: 'Eventos · Asistir',
  descripcion: 'Eventos presenciales y virtuales del Club: la vitrina del miembro, inscribirse y comprar entradas',
  icono: 'calendar',
  tipo: 'core',
  version: '0.1.0',

  // Producto para clientes desde el día uno (Ruta B) — excepción explícita al
  // default solo-staff de `.claude/patterns/modulo-nuevo.md`, igual que el
  // resto del Club. El kill switch real es `feature.eventos.core` + el plan.
  milbrandsInterno: false,

  routes: [
    // `enPortal: true` (Paso 5, 21-sep-2026): la vitrina y la ficha del MIEMBRO se
    // montan también en el portal del cliente (`/portal/eventos…`), con el mismo
    // componente. La puerta y la vista previa quedan del lado del staff.
    { path: 'eventos', enPortal: true, component: lazy(() => import('./pages/EventosPage')) },
    // Vista previa (staff): la ficha del asistente en pestaña propia, SIN el menú
    // del layout — `public: true` es solo de RENDER (patrón de Formación): los
    // datos los sirve `vistaPreviaDeEvento`, que exige sesión + staff del operador.
    { path: 'eventos/preview/:id', public: true, component: lazy(() => import('./pages/PreviewPage')) },
    // El link de una entrada regalada (Paso 6, ADR 2026-09-21 D4): PÚBLICA de
    // verdad — llega por mail a alguien que puede no tener cuenta. Lo que
    // muestra lo sirve `validarReclamo` (sin sesión, sin consumir nada); el
    // reclamo exige sesión con el mail verificado. Fuera del layout y del
    // portal a propósito: es una puerta, no una pantalla del módulo.
    { path: 'eventos/reclamar/:token', public: true, component: lazy(() => import('./pages/ReclamarEntradaPage')) },
    // El kit de un referente (marketing M6, ADR 2026-10-02 D8): PÚBLICA de
    // verdad — el organizador le pasa el link a quien participa o auspicia,
    // que puede no tener cuenta. Lo que muestra lo sirve `validarKitDeReferido`
    // (sin sesión, sólo material y su código; nada de estadísticas). Fuera del
    // layout y del portal, como el reclamo: es una puerta, no una pantalla.
    // El prefijo `kit/` no choca con `/e/**` (lo sirve la function).
    { path: 'kit/:token', public: true, component: lazy(() => import('./pages/KitDeReferidoPage')) },
    // Pasar a mi cuenta lo que compré sin cuenta (T10d-a c4, mismo molde): PÚBLICA
    // porque llega quien todavía no tiene cuenta, y el link del mail la abre con
    // `?t=<token>&p=<pedido>`. Lo que se pasa lo decide el server (sesión, mail
    // verificado y el token de un solo uso); sin sesión, la página manda al
    // login o al registro y vuelve acá con la query entera.
    { path: 'eventos/mis-compras', public: true, component: lazy(() => import('./pages/MisComprasPage')) },
    // La página pública del evento (T10a-7, ADR 2026-09-22 E7 + E11): una ruta
    // como DATO, sin componente. La SPA no la monta (App.jsx saltea las rutas sin
    // `component`): la sirve la function `servidaPor` detrás del rewrite de
    // Hosting `/e/**`, y un test cruza esta declaración con `firebase.json`
    // (`core/lib/rutasDeLanding.js`). El registry sigue siendo la única fuente
    // de qué rutas hay; cambia quién la sirve, no quién la declara.
    { path: 'e/:slug', landing: true, servidaPor: 'metaDeEvento' },
    // La ficha del asistente, dentro del layout (T3b).
    { path: 'eventos/:id', enPortal: true, component: lazy(() => import('./pages/EventoDetallePage')) },
    // La puerta (T5): escanear QRs. Quién puede lo decide el server (staff del
    // operador o permiso eventos-organizar.hacer-checkin, y el plan de la
    // empresa operadora con `eventos-organizar`).
    { path: 'eventos/checkin/:id', component: lazy(() => import('./pages/CheckinPage')) },
  ],

  // Sin menuItems propios: el ítem lo aporta el grupo `club`.
  menuItems: [],

  features: [],

  permisoModulos: ['eventos'],

}
